import { Box, Stack, Typography } from '@mui/material';
import { DataGridPremium, GridApiPremium, GridColDef } from '@mui/x-data-grid-premium';
import { StackedTextCell } from '@retail/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { useAssortmentTagEditActions } from '@retail/products/hooks';
import { useProductsTFunction } from '@retail/products/i18n';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { MutableRefObject, useMemo } from 'react';
import { AddTagMenu, AssortmentTagChip, RemoveTagMenu } from '../MgItem';

const baseColumnProps: Partial<GridColDef> = {
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  apiRef: MutableRefObject<GridApiPremium | null>;
  assortmentItems: AssortmentItemMinimal[];
  assortmentTags: AssortmentTag[];
  validFrom?: Date;
  validTo?: Date;
}

export function EditAssortmentTagsTable({
  apiRef,
  assortmentItems,
  assortmentTags,
  validFrom,
  validTo,
}: Props) {
  const t = useMyAssortmentTFunction();
  const productsT = useProductsTFunction();

  const { addTag, removeTag, addTagToAllItems, removeTagFromAllItems } =
    useAssortmentTagEditActions({ apiRef });

  const columns: GridColDef<AssortmentItemMinimal>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        width: 300,
        field: 'mgItem',
        sortable: true,
        headerName: t('myAssortment.columns.itemText'),
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={<Typography fontWeight="600">{row.primaryText}</Typography>}
            bottomLine={row.mgItemNumber}
          />
        ),
        valueGetter: (_, row) => row.mgItemNumber,
      },
      {
        ...baseColumnProps,
        width: 220,
        field: 'supplier',
        sortable: true,
        headerName: t('myAssortment.columns.supplier'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.supplier.name} bottomLine={row.supplier.id} />
        ),
        valueGetter: (_, row) => row.supplier.id,
      },
      {
        ...baseColumnProps,
        field: 'assortmentTags',
        flex: 1,
        minWidth: 300,
        headerName: t('myAssortment.centralAssortments.assortmentType.underAssortment'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={0.5}>
            <Typography variant="subtitle2">
              {t('myAssortment.centralAssortments.assortmentType.underAssortment')}
            </Typography>

            <AddTagMenu
              assortmentTags={assortmentTags}
              validFrom={validFrom}
              validTo={validTo}
              tooltip={productsT('products.mgItem.assortmentTags.addToAllItems')}
              handleTagChange={addTagToAllItems}
            />

            <RemoveTagMenu
              assortmentTags={assortmentTags}
              tooltip={productsT('products.mgItem.assortmentTags.removeFromAllItems')}
              handleRemoveTag={removeTagFromAllItems}
            />
          </Stack>
        ),
        renderCell: ({ row }) => {
          const activeTags = getActiveAssortmentTags(row.assortmentTags);

          return (
            <Stack flexDirection="row" alignItems="center" flexWrap="wrap" gap={0.5}>
              {activeTags.map((tag) => (
                <AssortmentTagChip
                  key={tag.tagId}
                  fullLabel
                  tagName={tag.tagName ?? tag.tagId + ''}
                  removeChip={() => removeTag(row, tag)}
                />
              ))}

              <AddTagMenu
                assortmentTags={assortmentTags}
                validFrom={validFrom}
                validTo={validTo}
                excludedTagIds={activeTags.map((tag) => tag.tagId)}
                handleTagChange={(tagToAdd: SupplierItemAssortmentTag) => addTag(row, tagToAdd)}
              />
            </Stack>
          );
        },
      },
    ],
    [
      addTag,
      addTagToAllItems,
      assortmentTags,
      productsT,
      removeTag,
      removeTagFromAllItems,
      t,
      validFrom,
      validTo,
    ]
  );

  return (
    <Stack flexGrow={1}>
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <DataGridPremium<AssortmentItemMinimal>
            apiRef={apiRef}
            rows={assortmentItems}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnReorder
            hideFooter
            rowHeight={65}
            getRowId={(row) => row.mgSupplierItemId}
            disableColumnFilter
            disableColumnMenu
          />
        </Box>
      </Box>
    </Stack>
  );
}
